/**
 * Bottom Sheet Roster Component
 *
 * A mobile-friendly bottom sheet containing the creature roster for deployment.
 * Supports peek mode (showing 3-4 thumbnails) and expanded mode (full scrollable list).
 *
 * Features:
 * - Swipe/drag to expand or collapse
 * - Peek mode shows thumbnail preview
 * - Sticky header shows selected creature when collapsed
 * - Works on both mobile (touch) and desktop (mouse)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useBottomSheet } from '../../hooks/useBottomSheet';
import { getCreatureLibraryService } from '../../services/creatureLibraryService';
import { GalleryCreatureCard } from './GalleryCreatureCard';
import { CreatureDetailModal } from './CreatureDetailModal';
import { RosterThumbnailRow } from './RosterThumbnailRow';
import { SelectionStickyHeader } from './SelectionStickyHeader';
import type { SerializedCreatureDocument } from '@shared/types/creature-storage';
import './BottomSheetRoster.css';

export interface BottomSheetRosterProps {
  /** Number of creatures currently deployed */
  deployedCount: number;
  /** Maximum creatures allowed (default: 6) */
  maxDeployment: number;
  /** Set of creature IDs already deployed */
  deployedCreatureIds: Set<string>;
  /** Called when drag starts on a creature card */
  onDragStart: (creature: SerializedCreatureDocument) => void;
  /** Called when drag ends */
  onDragEnd: () => void;
  /** Called when creature is selected (tap-to-select) */
  onSelect?: (creature: SerializedCreatureDocument) => void;
  /** Currently selected creature ID */
  selectedCreatureId?: string | null;
  /** Called to clear selection */
  onSelectionClear?: () => void;
}

// Mock creatures for demo/fallback
const MOCK_CREATURES: SerializedCreatureDocument[] = [
  {
    id: 'mock-warrior',
    ownerId: 'demo',
    name: 'Warrior',
    createdAt: new Date().toISOString(),
    generationJobId: 'mock',
    inputType: 'text',
    textDescription: 'A brave warrior',
    concept: 'warrior',
    race: 'human',
    class: 'fighter',
    abilities: ['Slash'],
    combatAttributes: [],
    animations: {},
    sprites: {
      menuSprite: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMzIiIGN5PSIzMiIgcj0iMjgiIGZpbGw9IiMzNDk4ZGIiLz48dGV4dCB4PSIzMiIgeT0iMzIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuMzVlbSIgZmlsbD0iI2ZmZiIgZm9udC1zaXplPSIyNCIgZm9udC13ZWlnaHQ9ImJvbGQiPlc8L3RleHQ+PC9zdmc+',
      directions: { E: { sprite: '', walkFrames: [], idleFrames: [], attackFrames: [] }, NE: { sprite: '', walkFrames: [], idleFrames: [], attackFrames: [] }, SE: { sprite: '', walkFrames: [], idleFrames: [], attackFrames: [] } }
    },
    version: '1.0.0',
    generationTimeMs: 0,
  },
];

export const BottomSheetRoster: React.FC<BottomSheetRosterProps> = ({
  deployedCount,
  maxDeployment,
  deployedCreatureIds,
  onDragStart,
  onDragEnd,
  onSelect,
  selectedCreatureId,
  onSelectionClear,
}) => {
  const [creatures, setCreatures] = useState<SerializedCreatureDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailCreature, setDetailCreature] = useState<SerializedCreatureDocument | null>(null);

  // Bottom sheet state
  const {
    mode,
    isDragging,
    dragOffset,
    handlers,
    expand,
    collapse,
    toggle,
  } = useBottomSheet({
    peekHeight: 130,
    snapThreshold: 60,
    velocityThreshold: 0.4,
  });

  // Get selected creature object
  const selectedCreature = selectedCreatureId
    ? creatures.find(c => c.id === selectedCreatureId)
    : null;

  // Load creatures from library
  useEffect(() => {
    loadCreatures();
  }, []);

  const loadCreatures = async () => {
    setLoading(true);
    setError(null);

    try {
      const libraryService = getCreatureLibraryService();
      const result = await libraryService.listCreatures({ limit: 100 });

      if (result.creatures.length === 0) {
        console.log('[BottomSheetRoster] Using mock creatures (empty gallery)');
        setCreatures(MOCK_CREATURES);
      } else {
        setCreatures(result.creatures);
      }
    } catch (err) {
      console.error('[BottomSheetRoster] Failed to load creatures:', err);
      setError('Failed to load creatures');
      setCreatures(MOCK_CREATURES);
    } finally {
      setLoading(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    collapse();
  }, [collapse]);

  // Handle ESC key to collapse
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mode === 'expanded') {
        collapse();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, collapse]);

  // Lock body scroll when expanded
  useEffect(() => {
    if (mode === 'expanded') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mode]);

  // Calculate dynamic transform for drag
  const sheetStyle: React.CSSProperties = isDragging
    ? { transform: `translateY(${dragOffset}px)` }
    : {};

  const isLimitReached = deployedCount >= maxDeployment;

  return (
    <>
      {/* Backdrop - visible when expanded */}
      <div
        className="bottom-sheet-backdrop"
        data-visible={mode === 'expanded'}
        onClick={handleBackdropClick}
      />

      {/* Main sheet container */}
      <div
        className="bottom-sheet"
        data-mode={mode}
        data-dragging={isDragging}
        style={sheetStyle}
      >
        {/* Drag handle */}
        <div
          className="sheet-drag-handle"
          {...handlers}
          onClick={toggle}
        >
          <div className="drag-indicator" />
        </div>

        {/* Sticky selection header - only in peek mode with selection */}
        {mode === 'peek' && selectedCreature && (
          <SelectionStickyHeader
            creature={selectedCreature}
            onClear={onSelectionClear}
          />
        )}

        {/* Peek mode: thumbnail row */}
        {mode === 'peek' && !selectedCreature && (
          <RosterThumbnailRow
            creatures={creatures}
            deployedCreatureIds={deployedCreatureIds}
            deployedCount={deployedCount}
            maxDeployment={maxDeployment}
            onSelect={onSelect}
            selectedCreatureId={selectedCreatureId}
            onExpand={expand}
            loading={loading}
          />
        )}

        {/* Expanded mode: full roster */}
        {mode === 'expanded' && (
          <div className="sheet-content">
            <div className="sheet-header">
              <h2 className="sheet-title">Your Army</h2>
              <div className={`deployment-counter ${isLimitReached ? 'limit-reached' : ''}`}>
                {deployedCount}/{maxDeployment} deployed
              </div>
            </div>

            {isLimitReached && (
              <div className="limit-warning">
                Maximum {maxDeployment} creatures deployed
              </div>
            )}

            {loading ? (
              <div className="sheet-loading">Loading creatures...</div>
            ) : error ? (
              <div className="sheet-error">
                <p>{error}</p>
                <button onClick={loadCreatures} className="retry-button">Retry</button>
              </div>
            ) : (
              <div className="sheet-grid">
                {creatures.map(creature => {
                  const isDeployed = deployedCreatureIds.has(creature.id);
                  const isDisabled = isLimitReached && !isDeployed;
                  const isSelected = selectedCreatureId === creature.id;

                  return (
                    <GalleryCreatureCard
                      key={creature.id}
                      creature={creature}
                      disabled={isDisabled}
                      deployed={isDeployed}
                      isSelected={isSelected}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                      onSelect={onSelect}
                      onViewDetails={() => setDetailCreature(creature)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Creature detail modal */}
      <CreatureDetailModal
        creature={detailCreature}
        onClose={() => setDetailCreature(null)}
      />
    </>
  );
};

export default BottomSheetRoster;
